import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { ChartContainer } from '@/components/ui/chart';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { Loader2, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { getRiskColor } from './riskUtils';
import { useLocalization } from '@/hooks/useLocalization';

export interface RiskFactorOverrideRow {
  technical_complexity: number | null;
  scope_creep: number | null;
}

export interface WorkloadFactor {
  openTasks: number;
  teamSize: number;
  ratio: number | null; // openTasks / teamSize, null when teamSize is 0
  normalizedScore: number; // 0-100, normalized against the portfolio's highest ratio
}

export interface BudgetFactor {
  ratioPct: number; // spent/budget * 100, clamped 0-100; 0 when no budget data
  hasBudgetData: boolean;
}

interface RiskFactorsPanelProps {
  projectId: string;
  dependenciesScore: number;
  budget: BudgetFactor;
  workload: WorkloadFactor;
  override: RiskFactorOverrideRow | null;
  canManage: boolean;
  onSaved: () => void;
}

const factorChartConfig = {
  score: { label: 'Score', color: 'hsl(var(--primary))' },
};

export const RiskFactorsPanel = ({
  projectId,
  dependenciesScore,
  budget,
  workload,
  override,
  canManage,
  onSaved,
}: RiskFactorsPanelProps) => {
  const { t } = useLocalization();
  const factors = [
    {
      key: 'dependencies',
      name: 'Dependencies',
      score: dependenciesScore,
      note: 'Derived from this project\'s open blocked-dependency signals.',
    },
    {
      key: 'budget',
      name: 'Budget Constraints',
      score: budget.ratioPct,
      note: budget.hasBudgetData
        ? `Spent vs budget ratio.`
        : 'No budget data recorded for this project.',
    },
    {
      key: 'workload',
      name: 'Workload',
      score: workload.normalizedScore,
      note:
        workload.ratio !== null
          ? `${workload.openTasks} open task${workload.openTasks === 1 ? '' : 's'} / ${workload.teamSize} team member${workload.teamSize === 1 ? '' : 's'} (relative to other projects).`
          : 'No team size recorded for this project.',
    },
    {
      key: 'technical_complexity',
      name: 'Technical Complexity',
      score: override?.technical_complexity ?? 0,
      note: override?.technical_complexity == null ? 'Not assessed' : 'Manually rated by leadership.',
      notAssessed: override?.technical_complexity == null,
    },
    {
      key: 'scope_creep',
      name: 'Scope Creep',
      score: override?.scope_creep ?? 0,
      note: override?.scope_creep == null ? 'Not assessed' : 'Manually rated by leadership.',
      notAssessed: override?.scope_creep == null,
    },
  ];

  const radarData = factors.map((f) => ({ factor: f.name.split(' ')[0], score: f.score, fullMark: 100 }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="h-64">
          <ChartContainer config={factorChartConfig}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="hsl(var(--border))" />
              <PolarAngleAxis dataKey="factor" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} />
              <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} />
              <Radar name="Score" dataKey="score" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.3} strokeWidth={2} />
            </RadarChart>
          </ChartContainer>
        </div>

        <div className="space-y-3">
          {factors.map((f) => (
            <div key={f.key} className="p-3 rounded-lg border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">{f.name}</span>
                {f.notAssessed && <Badge variant="outline" className="text-xs">{t('Not assessed')}</Badge>}
              </div>
              <div className="flex items-center gap-2">
                <Progress value={f.score} className="flex-1 h-2" />
                <span className={cn('text-sm font-bold w-8', getRiskColor(f.score))}>{f.score}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">{f.note}</p>
            </div>
          ))}
        </div>
      </div>

      {canManage && (
        <div className="flex justify-end">
          <RateFactorsDialog projectId={projectId} override={override} onSaved={onSaved} />
        </div>
      )}
    </div>
  );
};

const RateFactorsDialog = ({
  projectId,
  override,
  onSaved,
}: {
  projectId: string;
  override: RiskFactorOverrideRow | null;
  onSaved: () => void;
}) => {
  const { t } = useLocalization();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [technicalComplexity, setTechnicalComplexity] = useState(
    override?.technical_complexity != null ? String(override.technical_complexity) : '',
  );
  const [scopeCreep, setScopeCreep] = useState(
    override?.scope_creep != null ? String(override.scope_creep) : '',
  );

  const handleSave = async () => {
    setSaving(true);
    const tc = technicalComplexity.trim() === '' ? null : Math.max(0, Math.min(100, Number(technicalComplexity)));
    const sc = scopeCreep.trim() === '' ? null : Math.max(0, Math.min(100, Number(scopeCreep)));
    const { error } = await supabase.from('risk_factor_overrides').upsert(
      {
        project_id: projectId,
        technical_complexity: tc,
        scope_creep: sc,
        updated_by: user?.id ?? null,
      },
      { onConflict: 'tenant_id,project_id' },
    );
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t('Risk factors saved'));
    setOpen(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 text-xs"><Pencil className="w-3.5 h-3.5 me-1" />  {t('Rate')}</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{t('Rate Technical Complexity & Scope Creep')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            
            {t('No connector observes these two factors — enter a 0-100 rating directly, or leave blank to mark "Not assessed".')}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t('Technical Complexity')}</Label>
              <Input type="number" min={0} max={100} placeholder="0-100" value={technicalComplexity} onChange={(e) => setTechnicalComplexity(e.target.value)} />
            </div>
            <div>
              <Label>{t('Scope Creep')}</Label>
              <Input type="number" min={0} max={100} placeholder="0-100" value={scopeCreep} onChange={(e) => setScopeCreep(e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>{t('Cancel')}</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 me-2 animate-spin" />}  {t('Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RiskFactorsPanel;
